import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar, Container } from 'react-bootstrap';
import axios from 'axios';
import logo from '../../assets/logo.png';
import FeaturedManager from './FeaturedManager';

const ProductManager = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [deletedProducts, setDeletedProducts] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    category: '',
    description: '',
    image: '',
    spiceLevel: '',
    attributes: {}
  });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showAttributeManager, setShowAttributeManager] = useState(false);
  const [newAttributeName, setNewAttributeName] = useState('');
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    image: true,
    name: true,
    price: true,
    category: true,
    spiceLevel: true,
    createdAt: false,
    updatedAt: false
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showFeaturedManager, setShowFeaturedManager] = useState(false);


  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedProducts = (productList) => {
    if (!sortConfig.key) return productList;
    
    return [...productList].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const availableColumns = [
    { key: 'image', label: 'Image', required: true },
    { key: 'name', label: 'Name', required: true },
    { key: 'price', label: 'Price', required: true },
    { key: 'category', label: 'Category', required: false },
    { key: 'spiceLevel', label: 'Spice Level', required: false },
    { key: 'createdAt', label: 'Date Created', required: false },
    { key: 'updatedAt', label: 'Last Modified', required: false }
  ];

  // Common attributes that can be added to products
  const commonAttributes = [
    { key: 'weight', label: 'Weight', type: 'text', placeholder: 'e.g., 330 gms' },
    { key: 'sku', label: 'SKU', type: 'text', placeholder: 'e.g., STB_019' },
    { key: 'stockStatus', label: 'Stock Status', type: 'boolean' },
    { key: 'allergens', label: 'Allergens', type: 'text', placeholder: 'e.g., Contains nuts' },
    { key: 'ingredients', label: 'Main Ingredients', type: 'text', placeholder: 'e.g., Rice flour, spices' },
    { key: 'nutritionInfo', label: 'Nutrition Info', type: 'text', placeholder: 'e.g., 150 cal per serving' }
  ];

  useEffect(() => {
    fetchProducts();
    fetchDeletedProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchDeletedProducts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/products/deleted`);
      setDeletedProducts(response.data);
    } catch (error) {
      console.error('Error fetching deleted products:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    navigate('/');
  };

  // Attribute management functions
  const addAttribute = (attribute) => {
    const newAttributes = { ...formData.attributes };
    if (attribute.type === 'boolean') {
      newAttributes[attribute.key] = false;
    } else {
      newAttributes[attribute.key] = '';
    }
    setFormData({ ...formData, attributes: newAttributes });
    setShowAttributeManager(false);
  };

  const addCustomAttribute = () => {
    if (newAttributeName.trim()) {
      const newAttributes = { ...formData.attributes };
      newAttributes[newAttributeName.trim()] = '';
      setFormData({ ...formData, attributes: newAttributes });
      setNewAttributeName('');
      setShowAttributeManager(false);
    }
  };

  const updateAttribute = (key, value) => {
    const newAttributes = { ...formData.attributes };
    newAttributes[key] = value;
    setFormData({ ...formData, attributes: newAttributes });
  };

  const removeAttribute = (key) => {
    const newAttributes = { ...formData.attributes };
    delete newAttributes[key];
    setFormData({ ...formData, attributes: newAttributes });
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      // This will be implemented in backend
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      return response.data.imageUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let imageUrl = formData.image;
    
    // Upload new image if selected
    if (imageFile) {
      imageUrl = await handleImageUpload(imageFile);
      if (!imageUrl) return; // Upload failed
    }
    
    const productData = {
      ...formData,
      ...formData.attributes, // Spread attributes to top level
      price: parseFloat(formData.price),
      image: imageUrl
    };
    delete productData.attributes; // Remove the nested attributes object

    try {
      if (editingProduct) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/admin/products/${editingProduct.id}`, productData);
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/products`, {
          ...productData,
          id: Date.now().toString() // Simple ID generation
        });
      }
      
      fetchProducts();
      resetForm();
      alert(editingProduct ? 'Product updated!' : 'Product added!');
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    // Extract existing attributes from product
    const existingAttributes = {};
    Object.keys(product).forEach(key => {
      if (!['id', 'name', 'price', 'category', 'description', 'image', 'spiceLevel', 'createdAt', 'updatedAt'].includes(key)) {
        existingAttributes[key] = product[key];
      }
    });

    setFormData({
      name: product.name,
      price: product.price.toString(),
      category: product.category || '',
      description: product.description || '',
      image: product.image || '',
      spiceLevel: product.spiceLevel || '',
      attributes: existingAttributes
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product? It can be restored later.')) return;
    
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/products/${productId}`);
      fetchProducts();
      fetchDeletedProducts();
      alert('Product deleted! You can restore it from the Deleted Products section.');
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product');
    }
  };

  const handleRestore = async (productId) => {
    if (!window.confirm('Are you sure you want to restore this product?')) return;
    
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/admin/products/${productId}/restore`);
      fetchProducts();
      fetchDeletedProducts();
      alert('Product restored successfully!');
    } catch (error) {
      console.error('Error restoring product:', error);
      alert('Failed to restore product');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', price: '', category: '', description: '', image: '', spiceLevel: '', attributes: {} });
    setEditingProduct(null);
    setShowForm(false);
    setImageFile(null);
  };

  return (
    <>
      {/* Main Navigation */}
      <Navbar 
        expand="lg" 
        className="animated-nav-background"
        style={{ 
          boxShadow: '0 2px 10px rgba(232, 12, 129, 0.2)',
          padding: '1rem 0',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(232, 12, 129, 0.1)'
        }}
        variant="dark"
      >
        <Container>
          <Navbar.Brand href="/">
            <img 
              src={logo} 
              alt="Star Tidbits" 
              style={{ 
                height: '60px', 
                width: 'auto',
                maxWidth: '100%'
              }}
            />
          </Navbar.Brand>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', marginLeft: 'auto' }}>
            <h3 style={{ color: 'white', margin: 0, fontSize: '1.25rem' }}>Product Management</h3>
            <button 
              onClick={() => navigate('/admin/dashboard')}
              className="btn btn-outline"
              style={{ color: 'white', borderColor: 'white', fontSize: '0.875rem' }}
            >
              Dashboard
            </button>
            <button 
              onClick={handleLogout}
              className="btn btn-outline"
              style={{ color: 'white', borderColor: 'white', fontSize: '0.875rem' }}
            >
              Logout
            </button>
          </div>
        </Container>
      </Navbar>
      
      <div style={{ padding: '2rem 0', minHeight: 'calc(100vh - 80px)', backgroundColor: 'var(--neutral-light)' }}>
      <div className="container">

        {/* Action Buttons */}
        <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button 
            onClick={() => setShowForm(!showForm)}
            className="btn btn-primary"
          >
            {showForm ? 'Cancel' : '+ Add New Product'}
          </button>
          <button 
            onClick={() => setShowDeleted(!showDeleted)}
            className="btn btn-outline"
            style={{ backgroundColor: showDeleted ? 'var(--primary-saffron)' : 'transparent', color: showDeleted ? 'white' : 'var(--primary-saffron)' }}
          >
            {showDeleted ? 'Show Active Products' : `Show Deleted Products (${deletedProducts.length})`}
          </button>
          <button 
            onClick={() => setShowFeaturedManager(true)}
            className="btn btn-outline"
            style={{ color: 'var(--accent-green)', borderColor: 'var(--accent-green)' }}
          >
            ⭐ Manage Featured Products
          </button>

        </div>

        {/* Product Form */}
        {showForm && (
          <div className="card" style={{ marginBottom: '2rem' }}>
            <h3>{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-2" style={{ gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Product Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Price (S$) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-2" style={{ gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  >
                    <option value="">Select Category</option>
                    <option value="Murukku">Murukku</option>
                    <option value="Snacks">Snacks</option>
                    <option value="Sweets">Sweets</option>
                    <option value="Savories">Savories</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Spice Level
                  </label>
                  <select
                    value={formData.spiceLevel}
                    onChange={(e) => setFormData({...formData, spiceLevel: e.target.value})}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  >
                    <option value="">Select Spice Level</option>
                    <option value="Mild">🌶️ Mild</option>
                    <option value="Medium">🌶️🌶️ Medium</option>
                    <option value="Spicy">🌶️🌶️🌶️ Spicy</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Product Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0])}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
                {formData.image && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <img 
                      src={formData.image} 
                      alt="Current" 
                      style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '6px' }}
                    />
                  </div>
                )}
              </div>

              {/* Dynamic Attributes Section */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Additional Attributes</h4>
                  <button 
                    type="button"
                    onClick={() => setShowAttributeManager(true)} 
                    className="btn btn-outline"
                    style={{ fontSize: '0.75rem', padding: '0.5rem 1rem' }}
                  >
                    + Add Attribute
                  </button>
                </div>
                
                {/* Render active attributes */}
                {Object.entries(formData.attributes || {}).map(([key, value]) => (
                  <div key={key} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                    <input 
                      value={key} 
                      disabled 
                      style={{ width: '30%', padding: '0.5rem', backgroundColor: '#e9ecef', borderRadius: '4px', border: '1px solid #ced4da' }}
                    />
                    {typeof value === 'boolean' ? (
                      <select
                        value={value.toString()}
                        onChange={(e) => updateAttribute(key, e.target.value === 'true')}
                        style={{ width: '60%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                      >
                        <option value="true">Yes / In Stock</option>
                        <option value="false">No / Out of Stock</option>
                      </select>
                    ) : (
                      <input 
                        value={value} 
                        onChange={(e) => updateAttribute(key, e.target.value)}
                        style={{ width: '60%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                        placeholder={`Enter ${key}`}
                      />
                    )}
                    <button 
                      type="button"
                      onClick={() => removeAttribute(key)} 
                      className="btn"
                      style={{ backgroundColor: '#dc3545', color: 'white', padding: '0.5rem', borderRadius: '4px', width: '10%' }}
                    >
                      ×
                    </button>
                  </div>
                ))}
                
                {Object.keys(formData.attributes || {}).length === 0 && (
                  <p style={{ color: '#6c757d', fontStyle: 'italic', margin: 0 }}>No additional attributes added</p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : (editingProduct ? 'Update Product' : 'Add Product')}
                </button>
                <button 
                  type="button" 
                  onClick={resetForm}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Column Selector */}
        {showColumnSelector && !showDeleted && (
          <div className="card" style={{ marginBottom: '2rem' }}>
            <h4>Select Columns to Display</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
              {availableColumns.map(column => (
                <label key={column.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: column.required ? 'not-allowed' : 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={visibleColumns[column.key]}
                    onChange={(e) => !column.required && setVisibleColumns({...visibleColumns, [column.key]: e.target.checked})}
                    disabled={column.required}
                    style={{ cursor: column.required ? 'not-allowed' : 'pointer' }}
                  />
                  <span style={{ color: column.required ? '#999' : 'inherit' }}>
                    {column.label} {column.required && '(Required)'}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Products List */}
        <div className="card" style={{ position: 'relative' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>{showDeleted ? `Deleted Products (${deletedProducts.length})` : `Active Products (${products.length})`}</h3>
            {!showDeleted && (
              <span 
                onClick={() => setShowColumnSelector(!showColumnSelector)}
                style={{ 
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: showColumnSelector ? 'var(--accent-green)' : '#666',
                  transition: 'color 0.2s'
                }}
                title="Select columns to display"
              >
                ⚙️
              </span>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee' }}>
                  {visibleColumns.image && <th style={{ padding: '0.75rem', textAlign: 'left' }}>Image</th>}
                  {visibleColumns.name && (
                    <th 
                      style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('name')}
                    >
                      Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.price && (
                    <th 
                      style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('price')}
                    >
                      Price {sortConfig.key === 'price' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.category && (
                    <th 
                      style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('category')}
                    >
                      Category {sortConfig.key === 'category' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.spiceLevel && <th style={{ padding: '0.75rem', textAlign: 'left' }}>Spice Level</th>}
                  {visibleColumns.createdAt && (
                    <th 
                      style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('createdAt')}
                    >
                      Date Created {sortConfig.key === 'createdAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.updatedAt && (
                    <th 
                      style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('updatedAt')}
                    >
                      Last Modified {sortConfig.key === 'updatedAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getSortedProducts(showDeleted ? deletedProducts : products).map(product => (
                  <tr key={product.id} style={{ borderBottom: '1px solid #eee' }}>
                    {visibleColumns.image && (
                      <td style={{ padding: '0.75rem' }}>
                        {product.image ? (
                          <img 
                            src={product.image} 
                            alt={product.name}
                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                        ) : (
                          <div style={{ 
                            width: '50px', 
                            height: '50px', 
                            backgroundColor: '#f0f0f0', 
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem'
                          }}>
                            No Image
                          </div>
                        )}
                      </td>
                    )}
                    {visibleColumns.name && <td style={{ padding: '0.75rem', fontWeight: '600' }}>{product.name}</td>}
                    {visibleColumns.price && <td style={{ padding: '0.75rem' }}>S${product.price?.toFixed(2)}</td>}
                    {visibleColumns.category && <td style={{ padding: '0.75rem' }}>{product.category || 'N/A'}</td>}
                    {visibleColumns.spiceLevel && (
                      <td style={{ padding: '0.75rem' }}>
                        {product.spiceLevel ? (
                          <span style={{ 
                            backgroundColor: product.spiceLevel === 'Spicy' ? '#ff5722' : 
                                            product.spiceLevel === 'Medium' ? '#ff9800' : '#4caf50',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem'
                          }}>
                            {product.spiceLevel}
                          </span>
                        ) : 'N/A'}
                      </td>
                    )}
                    {visibleColumns.createdAt && (
                      <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                        {product.createdAt ? new Date(product.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }) : 'N/A'}
                      </td>
                    )}
                    {visibleColumns.updatedAt && (
                      <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                        {product.updatedAt ? new Date(product.updatedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }) : 'N/A'}
                      </td>
                    )}
                    <td style={{ padding: '0.75rem' }}>
                      {showDeleted ? (
                        <button 
                          onClick={() => handleRestore(product.id)}
                          style={{ 
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.2rem',
                            color: 'var(--accent-green)',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          title="Restore Product"
                        >
                          ↩️
                        </button>
                      ) : (
                        <>
                          <button 
                            onClick={() => handleEdit(product)}
                            style={{ 
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '1.2rem',
                              color: '#666',
                              padding: '0.5rem',
                              marginRight: '0.5rem',
                              borderRadius: '4px',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            title="Edit Product"
                          >
                            ✏️
                          </button>
                          <button 
                            onClick={() => handleDelete(product.id)}
                            style={{ 
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '1.2rem',
                              color: '#f44336',
                              padding: '0.5rem',
                              borderRadius: '4px',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#ffebee'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            title="Delete Product"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Featured Products Manager */}
        {showFeaturedManager && (
          <FeaturedManager 
            products={products}
            onClose={() => setShowFeaturedManager(false)}
            onUpdate={() => {
              fetchProducts();
              fetchDeletedProducts();
            }}
          />
        )}

        {/* Attribute Manager Modal */}
        {showAttributeManager && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: 0 }}>Manage Product Attributes</h3>
                <button 
                  onClick={() => setShowAttributeManager(false)}
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
              
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '1rem' }}>Common Attributes:</h4>
                {commonAttributes.map(attr => (
                  <div key={attr.key} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '0.75rem', 
                    marginBottom: '0.5rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px'
                  }}>
                    <div>
                      <strong>{attr.label}</strong>
                      <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                        {attr.placeholder || `${attr.type} field`}
                      </div>
                    </div>
                    <button 
                      onClick={() => addAttribute(attr)} 
                      className="btn btn-primary"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                      disabled={formData.attributes && formData.attributes.hasOwnProperty(attr.key)}
                    >
                      {formData.attributes && formData.attributes.hasOwnProperty(attr.key) ? 'Added' : 'Add'}
                    </button>
                  </div>
                ))}
              </div>
              
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '1rem' }}>Add Custom Attribute:</h4>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input 
                    type="text"
                    placeholder="Attribute Name (e.g., 'expiry date')"
                    value={newAttributeName}
                    onChange={(e) => setNewAttributeName(e.target.value)}
                    style={{ 
                      flex: 1, 
                      padding: '0.75rem', 
                      borderRadius: '6px', 
                      border: '1px solid #ddd' 
                    }}
                  />
                  <button 
                    onClick={addCustomAttribute}
                    className="btn btn-primary"
                    disabled={!newAttributeName.trim()}
                  >
                    Add
                  </button>
                </div>
              </div>
              
              <div style={{ textAlign: 'right' }}>
                <button 
                  onClick={() => setShowAttributeManager(false)}
                  className="btn btn-outline"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default ProductManager;